import clsx from 'clsx';
import React, { memo, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Navigate } from 'react-router-dom';
import { NEW_PORTAL_HEADER } from 'reactApp/appHelpers/configHelpers';
import { forceVigboLinkedAccont } from 'reactApp/appHelpers/featuresHelpers/features/vigbo';
import { useVigboTariff } from 'reactApp/hooks/useVigboTariff';
import { routeChangeSuccess } from 'reactApp/modules/router/router.module';
import { EStorageType } from 'reactApp/modules/storage/storage.types';
import { loadVigboAlbumsListRequest } from 'reactApp/modules/vigbo/vigbo.module';
import { getVigboAlbums, getVigboLinkState, getVigboLoadingState } from 'reactApp/modules/vigbo/vigbo.selectors';
import { VigboAccountLinkState } from 'reactApp/modules/vigbo/vigbo.types';
import { renderNewVigboAlbumModal } from 'reactApp/sections/VigboPage/renderNewVigboAlbumModal';
import { AlbumsGrid } from 'reactApp/ui/AlbumsGrid/AlbumsGrid';
import { BreadcrumbsContainer } from 'reactApp/ui/BreadcrumbsContainer/BreadcrumbsContainer';
import { Empty } from 'reactApp/ui/Empty/Empty';
import { EmptyFinishedVigbo } from 'reactApp/ui/EmptyFinishedVigbo/EmptyFinishedVigbo';
import { Footer } from 'reactApp/ui/Footer/Footer';
import { Spinner } from 'reactApp/ui/Spinner/Spinner';
import { ECategoryGa, sendPaymentGa } from 'reactApp/utils/paymentGa';

import styles from './VigboPage.css';

export const VigboPage = memo(function VigboPage() {
    const dispatch = useDispatch();

    const accountLinkState = useSelector(getVigboLinkState);
    const isLinkedAccount = forceVigboLinkedAccont || accountLinkState === VigboAccountLinkState.linked;

    const vigboAlbums = useSelector(getVigboAlbums);
    const { isLoading: isVigboAlbumsLoading, isLoaded } = useSelector(getVigboLoadingState);
    const { isLoading: isVigboTariffLoading, isVigboActiveTariff, isVigboFinishedTariff } = useVigboTariff();

    const isLoading = isVigboAlbumsLoading || isVigboTariffLoading;
    const isEmpty = vigboAlbums.length === 0 || !isLoaded;

    useEffect(() => {
        sendPaymentGa({ eventCategory: ECategoryGa.entered, action: 'vigbo' });
        sendPaymentGa({ eventCategory: ECategoryGa.entered, action: 'page-entered', source: 'vigbo' });
        if (!isLoading && (isVigboActiveTariff || isVigboFinishedTariff) && !isLinkedAccount) {
            sendPaymentGa({
                eventCategory: ECategoryGa.vigbo,
                action: 'button-connection-show',
            });
        }
        sendPaymentGa({
            eventCategory: ECategoryGa.entered,
            action: 'section',
            section: 'vigbo',
            connection: 0,
        });
        sendPaymentGa({
            eventCategory: ECategoryGa.entered,
            action: 'vigbo',
            section: 'vigbo',
            connection: 0,
        });
        sendPaymentGa({
            eventCategory: ECategoryGa.entered,
            action: 'page-entered',
            source: 'vigbo',
            connection: 0,
        });
        dispatch(routeChangeSuccess({ id: '/', storage: EStorageType.vigbo, params: {}, __isFolder: true, url: '', __parent: '' }));
        dispatch(loadVigboAlbumsListRequest());
    }, []);

    const createVigboAlbum = () => {
        renderNewVigboAlbumModal({ navigateToAlbum: true });
        sendPaymentGa({
            eventCategory: ECategoryGa.vigbo,
            action: 'create-gallery-click',
            connection_vigbo: 0,
        });
    };

    const restrictVigboAlbumCreation = () => {
        /** TODO: Добавить логику */
    };

    const emptyContent = useMemo(() => {
        if (!isLinkedAccount) {
            return <Empty.New storage={EStorageType.vigbo} categoryType={VigboAccountLinkState.unlinked} />;
        }

        if (isVigboActiveTariff) {
            return <Empty.New storage={EStorageType.vigbo} categoryType={VigboAccountLinkState.linked} />;
        }

        if (isVigboFinishedTariff) {
            return (
                <div className={styles.emptyContent}>
                    <EmptyFinishedVigbo />
                </div>
            );
        }
    }, [isLinkedAccount, isVigboActiveTariff, isVigboFinishedTariff]);

    if (isLoading) {
        return <Spinner />;
    }

    if (!isVigboActiveTariff && !isVigboFinishedTariff) {
        return <Navigate replace to="/home/" />;
    }

    return (
        <div
            className={clsx(styles.root, {
                [styles.root_new_portal_header]: NEW_PORTAL_HEADER,
            })}
        >
            <div
                className={clsx(styles.wrapper, {
                    [styles.emptyWrapper]: isEmpty,
                })}
            >
                <div className={styles.header}>
                    <BreadcrumbsContainer />
                </div>

                {isEmpty || !isLinkedAccount ? (
                    emptyContent
                ) : (
                    <div className={styles.grid_container}>
                        <AlbumsGrid
                            handleCreateClick={!isVigboActiveTariff ? restrictVigboAlbumCreation : createVigboAlbum}
                            disableCreateButton={!isVigboActiveTariff}
                            createButtonText="Создать галерею Vigbo"
                            albums={vigboAlbums}
                        />
                    </div>
                )}

                <Footer storage={EStorageType.vigbo} isAbsolutePos={true} />
            </div>
        </div>
    );
});
